
import React, { useState, useEffect, useMemo } from 'react';
import { adminUsersService } from '../../services/admin.users.service';
import { adminTradingService } from '../../services/admin.trading.service';
import { adminBtcService } from '../../services/admin.btc.service';
import { adminKycService } from '../../services/admin.kyc.service';
import { adminWithdrawalService } from '../../services/admin.withdrawal.service';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import {
  Users,
  Activity,
  Coins,
  TrendingUp,
  ShieldCheck,
  Wallet,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Database,
  Cpu,
  RefreshCcw,
  Loader2
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Stats State
  const [userStats, setUserStats] = useState({ total: 0, active: 0 });
  const [tradeStats, setTradeStats] = useState({ total: 0, revenue: 0 });
  const [kycQueue, setKycQueue] = useState({ pending: 0, total: 0 });
  const [withdrawalQueue, setWithdrawalQueue] = useState({ pending: 0, total: 0 });

  // BTC Address State
  const [btcAddress, setBtcAddress] = useState("Loading...");
  const [isEditingBtc, setIsEditingBtc] = useState(false);
  const [isSavingBtc, setIsSavingBtc] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // System Health (Placeholder for now)
  const system = {
    status: 'OPTIMAL',
    uptime: '99.99%',
    lastBackup: '14m ago',
    activeAlerts: ['Geo-Sync Active', 'Auth Guard Enabled']
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usersRes, tradesRes, btcRes, kycRes, withdrawalRes] = await Promise.all([
        adminUsersService.listUsers({ limit: 1000 }),
        adminTradingService.getTradingLedger(),
        adminBtcService.getBtcAddress(),
        adminKycService.getKycRequests({ limit: 1000 }),
        adminWithdrawalService.listAllWithdrawals({ limit: 1000 })
      ]);

      // Process Users
      if (usersRes?.success && Array.isArray(usersRes.data)) {
        setUserStats({
          total: usersRes.data.length,
          active: usersRes.data.filter((u: any) => u.status === 'ACTIVE').length
        });
      }

      // Process Trades
      if (Array.isArray(tradesRes)) {
        const total = tradesRes.reduce((sum, u) => sum + parseInt(u.executionsCount || '0'), 0);
        const revenue = tradesRes.reduce((sum, u) => sum + parseFloat(u.netPnl || '0'), 0);
        setTradeStats({ total, revenue });
      }

      // Process BTC
      if (btcRes?.success) {
        setBtcAddress(btcRes.data.address);
      }

      // Process KYC
      if (kycRes?.success && Array.isArray(kycRes.data)) {
        setKycQueue({
          pending: kycRes.data.filter(k => k.status === 'PENDING').length,
          total: kycRes.data.length
        });
      }

      // Process Withdrawals
      if (withdrawalRes?.success && Array.isArray(withdrawalRes.data)) {
        setWithdrawalQueue({
          pending: withdrawalRes.data.filter(w => w.status === 'PENDING').length,
          total: withdrawalRes.data.length
        });
      }

    } catch (error) {
      console.error('Admin Dashboard Sync Failure:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateBtc = async () => {
    if (!btcAddress.trim()) return;
    setIsSavingBtc(true);
    try {
      const res = await adminBtcService.updateBtcAddress(btcAddress);
      if (res.success) {
        setIsEditingBtc(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error('BTC Update Failed:', error);
    } finally {
      setIsSavingBtc(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="animate-spin text-brand-primary" size={48} />
        <p className="text-brand-muted text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Synchronizing Admin Protocols...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-12">

      {/* 1. System Status Banner */}
      <section className="bg-brand-card border border-brand-border rounded-3xl p-6 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden group">
        <div className="absolute inset-0 bg-brand-primary/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="flex items-center gap-4 relative z-10">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${system.status === 'OPTIMAL' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
            <Cpu size={24} className={system.status === 'OPTIMAL' ? 'animate-pulse' : ''} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-white font-bold text-lg uppercase tracking-tight">Nexus Engine Status</h2>
              <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${system.status === 'OPTIMAL' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                {system.status}
              </span>
            </div>
            <p className="text-brand-muted text-xs font-medium">Uptime: {system.uptime} • Last Integrity Sync: {system.lastBackup}</p>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 relative z-10">
          {system.activeAlerts.map((alert, i) => (
            <div key={i} className="flex items-center gap-2 px-4 py-2 bg-brand-dark border border-brand-border rounded-xl">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-primary animate-pulse"></div>
              <span className="text-[10px] text-brand-muted font-bold uppercase tracking-widest">{alert}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 2. Platform Analytics Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnalyticCard
          label="Total Member Entities"
          value={userStats.total.toLocaleString()}
          icon={<Users size={20} />}
          subValue="Live Sync"
        />
        <AnalyticCard
          label="Active Terminal Sessions"
          value={userStats.active.toLocaleString()}
          icon={<Activity size={20} />}
          color="text-brand-primary"
        />
        <AnalyticCard
          label="Aggregated Executions"
          value={tradeStats.total.toLocaleString()}
          icon={<RefreshCcw size={20} />}
        />
        <AnalyticCard
          label="Platform Revenue (Net)"
          value={`$${tradeStats.revenue.toLocaleString()}`}
          icon={<TrendingUp size={20} />}
          color="text-emerald-500"
          subValue="Real-time"
        />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* 3. Trader BTC Address Management */}
        <div className="lg:col-span-8">
          <section className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <Database size={120} />
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <h3 className="text-xl font-bold text-white tracking-tight uppercase flex items-center gap-2">
                  <Wallet size={20} className="text-brand-primary" />
                  Primary Liquidity Vault
                </h3>
                <p className="text-[10px] text-brand-muted uppercase font-bold tracking-[0.2em] mt-1">Global System Deposit Protocol</p>
              </div>
              {showSuccess && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl animate-in zoom-in duration-300">
                  <CheckCircle2 size={14} className="text-emerald-500" />
                  <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Protocol Updated</span>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-brand-dark/50 border border-brand-border rounded-2xl">
                {isEditingBtc ? (
                  <div className="space-y-4">
                    <Input
                      label="New Target BTC Protocol Address"
                      value={btcAddress}
                      onChange={(e) => setBtcAddress(e.target.value)}
                      placeholder="Enter bc1q..."
                      className="font-mono text-sm"
                    />
                    <div className="flex gap-3">
                      <Button onClick={handleUpdateBtc} isLoading={isSavingBtc} className="flex-1 h-12">
                        COMMIT UPDATE
                      </Button>
                      <Button variant="secondary" onClick={() => setIsEditingBtc(false)} className="px-6 h-12">
                        CANCEL
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                    <div className="space-y-1">
                      <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Current Active Address</p>
                      <code className="text-lg font-mono font-bold text-white break-all">{btcAddress}</code>
                    </div>
                    <Button variant="secondary" onClick={() => setIsEditingBtc(true)} className="px-8 h-12 whitespace-nowrap">
                      ROTATE ADDRESS
                    </Button>
                  </div>
                )}
              </div>

              <div className="bg-brand-primary/5 border border-brand-primary/10 p-5 rounded-2xl flex items-start gap-4">
                <AlertTriangle size={20} className="text-brand-primary shrink-0 mt-0.5" />
                <p className="text-[10px] text-brand-muted font-bold uppercase tracking-widest leading-relaxed">
                  Notice: Modifying the Primary Liquidity Vault address affects all user deposit terminals globally.
                  Address changes are cryptographically propagated and verified across all Nexus nodes.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* 4. Queue Summaries */}
        <div className="lg:col-span-4 space-y-6">
          <QueueSummaryCard
            title="KYC Compliance Vault"
            pending={kycQueue.pending}
            approved={kycQueue.total}
            icon={<ShieldCheck size={18} />}
            ctaLabel="Audit Vault"
          />
          <QueueSummaryCard
            title="Treasury Outflows"
            pending={withdrawalQueue.pending}
            approved={withdrawalQueue.total}
            icon={<Coins size={18} />}
            ctaLabel="Authorize Payouts"
          />
        </div>
      </div>
    </div>
  );
};

const AnalyticCard: React.FC<{ label: string, value: string, icon: React.ReactNode, color?: string, subValue?: string }> = ({ label, value, icon, color = 'text-white', subValue }) => (
  <div className="bg-brand-card border border-brand-border p-6 rounded-3xl hover:border-brand-primary/30 transition-all duration-300 group">
    <div className="text-brand-muted mb-4 p-2 bg-brand-dark w-fit rounded-xl border border-brand-border group-hover:text-brand-primary transition-colors">
      {icon}
    </div>
    <p className="text-[10px] text-brand-muted uppercase font-black tracking-[0.2em] mb-1">{label}</p>
    <div className="flex items-end justify-between gap-2">
      <h3 className={`text-2xl font-bold tracking-tighter ${color}`}>{value}</h3>
      {subValue && <span className="text-[9px] font-black text-emerald-500 uppercase bg-emerald-500/10 px-2 py-0.5 rounded-full mb-1">{subValue}</span>}
    </div>
  </div>
);

const QueueSummaryCard: React.FC<{ title: string, pending: number, approved: number, icon: React.ReactNode, ctaLabel: string }> = ({ title, pending, approved, icon, ctaLabel }) => (
  <div className="bg-brand-card border border-brand-border rounded-3xl p-6 shadow-lg flex flex-col justify-between group hover:border-brand-primary/20 transition-all">
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="text-brand-primary">{icon}</div>
        <h3 className="text-sm font-black text-white uppercase tracking-wider">{title}</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="space-y-1">
          <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Pending Audit</p>
          <p className="text-2xl font-bold text-brand-primary">{pending}</p>
        </div>
        <div className="space-y-1">
          <p className="text-[9px] text-brand-muted font-black uppercase tracking-widest">Lifetime Total</p>
          <p className="text-2xl font-bold text-white">{approved.toLocaleString()}</p>
        </div>
      </div>
    </div>

    <button className="w-full flex items-center justify-between px-4 py-3 bg-brand-dark border border-brand-border rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-brand-muted group-hover:text-white group-hover:border-brand-primary/30 transition-all">
      {ctaLabel}
      <ChevronRight size={14} />
    </button>
  </div>
);

export default AdminDashboard;
